import React from 'react';
import { GoogleMap as GoogleMapComponent, LoadScript, Marker } from '@react-google-maps/api';
import { Box, Text, Center, Loader, Stack, Button } from '@mantine/core';

interface GoogleMapProps {
  address: string;
  apiKey: string;
  height?: number | string;
  width?: number | string;
}

const defaultMapStyles = {
  width: '100%',
  height: '350px',
  borderRadius: '8px',
  overflow: 'hidden',
};

const center = {
  lat: 51.0130,  // Calgary coordinates
  lng: -114.0854
};

const GoogleMap: React.FC<GoogleMapProps> = ({ 
  address, 
  apiKey,
  height = defaultMapStyles.height,
  width = defaultMapStyles.width 
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [map, setMap] = React.useState<google.maps.Map | null>(null);

  const mapStyles = {
    ...defaultMapStyles,
    height,
    width,
  };

  const onLoad = React.useCallback((map: google.maps.Map) => {
    setMap(map);
    setIsLoaded(true);
  }, []);

  const onUnmount = React.useCallback(() => {
    setMap(null);
  }, []);

  if (error) {
    return (
      <Box style={mapStyles} bg="gray.1">
        <Center h="100%">
          <Stack align="center" gap="md">
            <Text c="red.6">{error}</Text>
            <Button 
              component="a" 
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
            >
              Open in Google Maps
            </Button>
          </Stack>
        </Center>
      </Box>
    );
  }

  return (
    <LoadScript 
      googleMapsApiKey={apiKey}
      onError={(err: Error) => {
        console.error('Google Maps loading error:', err);
        setError('Unable to load Google Maps. Click below to view location.');
      }}
    >
      {!isLoaded ? (
        <Box style={mapStyles} bg="gray.1">
          <Center h="100%">
            <Loader size="lg" />
          </Center>
        </Box>
      ) : null}
      <GoogleMapComponent
        mapContainerStyle={mapStyles}
        center={center}
        zoom={15}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: true,
          fullscreenControl: true,
        }}
      >
        <Marker
          position={center}
          title={address}
        />
      </GoogleMapComponent>
    </LoadScript>
  );
};

export default GoogleMap; 